import React, { useState, useEffect } from "react";
import { Radio, notification } from "antd";
import type { RadioChangeEvent } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { subDays } from "date-fns";

import { Button, Box, Input, DateRangePicker, ScrollToTop } from "components";
import { CloseIcon, SendCompleteIcon, ConfirmFailIcon } from "icons";
import { localStorageHelper, handleConvertMonthYear } from "utils";
import ConfirmModal from "screens/changeStudentAffiliation/ConfirmModal";

import { UpdateStressCheckData } from "models/stressCheck/updateStressCheck";
import {
  CreateStressCheckActionCreators,
  StressCheckDetailsActionCreators,
  UpdateStressCheckActionCreators,
} from "redux/rootActions";
import { stressCheckDetailsDataResponse } from "redux/stressCheck/stressCheckDetails/stressCheckDetailsStates";
import {
  selectIsUpdated,
  selectIsUpdatingStressCheck,
  selectUpdateErrorMessage,
} from "redux/stressCheck/updateStressCheck/updateStressCheckStates";

import {
  AudioContainer,
  LeftItem,
  RightItem,
  TextTitle,
  AudioWrapper,
  TitleContainerClose,
  DateRangeItem,
  ButtonsContainer,
  ItemsContainerNumber,
} from "screens/mentalHealth/MentalHealth.style";

interface IEditDraft {
  id?: number;
  handleCancel: () => void;
}

const EditDraft: React.FC<IEditDraft> = ({ handleCancel, id }) => {
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const dispatch = useDispatch();
  const stressCheckDetailsData = useSelector(stressCheckDetailsDataResponse);
  const stressDraftUpdated = useSelector(selectIsUpdated);
  const stressDraftUpdating = useSelector(selectIsUpdatingStressCheck);
  const updateStressDraftError = useSelector(selectUpdateErrorMessage);
  const [examinationPassed, setExaminationPassed] = useState<boolean>(false);
  const [testName, setTestName] = useState<string>("");
  const [radioValue, setRadioValue] = useState<number>(1);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(moment().add(1, "M").toDate());
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [isVisibleModalLeave, setIsVisibleModalLeave] =
    useState<boolean>(false);
  const [error, setError] = useState({
    status: "",
    error: "",
  });
  const [inProgress, setInProgress] = useState<boolean>(true);

  const onChangeRadio = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value);
  };

  const onChangeTestName = (e: any) => {
    setTestName(e.target.value);
    if (error.status === "test-name") {
      setError({
        status: "",
        error: "",
      });
    }
  };

  const onSaveDraft = () => {
    const updateDraftParams: UpdateStressCheckData = {
      id: id,
      stress_check_type_id: radioValue,
      name: testName,
      start_time: moment(startDate).toString(),
      end_time: moment(endDate).toString(),
      temp_save_to_permant_save: "true",
    };
    dispatch(
      UpdateStressCheckActionCreators.updateStressCheckAction(updateDraftParams)
    );
  };

  const handleOpenModal = () => {
    if (!testName) {
      setError({
        status: "test-name",
        error: "enter-test-name",
      });
      return;
    }
    if (examinationPassed) {
      setError({
        status: "passed",
        error: "exam-period-passed",
      });
      return;
    }
    if (moment(startDate).diff(moment(new Date())) > 0) {
      setInProgress(false);
    } else {
      setInProgress(true);
    }
    setIsVisibleModal(true);
  };

  const handleConfirm = () => {
    onSaveDraft();
    setIsVisibleModal(false);
  };

  const handleSaveTempDraft = () => {
    if (!testName) {
      setError({
        status: "test-name",
        error: "enter-test-name",
      });
      return;
    }
    if (examinationPassed) {
      setError({
        status: "passed",
        error: "exam-period-passed",
      });
      return;
    }

    const updateTempDraftParams: UpdateStressCheckData = {
      id: id,
      stress_check_type_id: radioValue,
      name: testName,
      start_time: moment(startDate).toString(),
      end_time: moment(endDate).toString(),
      temp_save_to_permant_save: "false",
    };
    dispatch(
      UpdateStressCheckActionCreators.updateStressCheckAction(
        updateTempDraftParams
      )
    );
  };

  useEffect(() => {
    if (moment(endDate).diff(moment(subDays(new Date(), 1))) < 0) {
      setExaminationPassed(true);
    } else {
      setExaminationPassed(false);
      setError({
        status: "",
        error: "",
      });
    }
  }, [endDate]);

  useEffect(() => {
    if (stressDraftUpdated) {
      notification.success({
        placement: "bottomRight",
        message: t1("created"),
        icon: <SendCompleteIcon />,
      });
      dispatch(UpdateStressCheckActionCreators.handleResetDataAction());
      handleCancel();
    }
  }, [stressDraftUpdated]);

  useEffect(() => {
    if (updateStressDraftError) {
      notification.error({
        placement: "bottomRight",
        message: updateStressDraftError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(UpdateStressCheckActionCreators.handleResetDataAction());
    }
  }, [updateStressDraftError]);

  useEffect(() => {
    if (id) {
      dispatch(
        StressCheckDetailsActionCreators.stressCheckDetailsAction({
          id: id,
          language_code: currentLanguage,
        })
      );
    }
  }, [id]);

  useEffect(() => {
    if (stressCheckDetailsData && stressCheckDetailsData?.result) {
      const { name, examination_period, stress_check_type_id } =
        stressCheckDetailsData?.result;
      setRadioValue(stress_check_type_id);
      setTestName(name);
      const examinationPeriod: string[] = examination_period.split("〜");
      if (examinationPeriod.length) {
        setStartDate(moment(examinationPeriod[0]).toDate());
        setEndDate(moment(examinationPeriod[1]).toDate());
      }
    }
  }, [stressCheckDetailsData]);

  useEffect(() => {
    dispatch(CreateStressCheckActionCreators.handleResetDataAction());
    setError({
      status: "",
      error: "",
    });
    setIsVisibleModal(false);
    setRadioValue(1);
    setTestName("");
    setStartDate(new Date());
    setEndDate(moment().add(1, "M").toDate());
  }, []);

  return (
    <>
      <ScrollToTop />
      <Box title={t("stress-check-management")} padding="16px 16px 32px">
        <TitleContainerClose
          style={{
            paddingBottom: 24,
            marginTop: 8,
            marginBottom: 0,
            borderBottom: "1px solid #F2F2F2",
          }}
        >
          <TextTitle>{t("new-stress-check")}</TextTitle>
          <CloseIcon
            fill="#3B3B3B"
            style={{
              marginRight: 8,
              cursor: "pointer",
            }}
            onClick={() => setIsVisibleModalLeave(true)}
          />
        </TitleContainerClose>
        <ItemsContainerNumber
          style={{
            paddingTop: 22,
            paddingBottom: 22,
          }}
        >
          <LeftItem>{t1("action-item-number")}</LeftItem>
          <Radio.Group onChange={onChangeRadio} value={radioValue}>
            <AudioWrapper>
              <Radio value={1}>{t("25-questions")}</Radio>
              <Radio value={2}>{t("59-questions")}</Radio>
            </AudioWrapper>
          </Radio.Group>
        </ItemsContainerNumber>
        <AudioContainer>
          <LeftItem>{t1("test-name")}</LeftItem>
          <RightItem>
            <Input
              height={40}
              value={testName}
              onChange={onChangeTestName}
              placeholder={`${handleConvertMonthYear(
                moment(startDate).format("YYYY-MM-DD"),
                currentLanguage
              )}${t("stress-check-placeholder")}`}
            />
            {error.status === "test-name" && error.error && (
              <div className="red-text">{t1(error.error)}</div>
            )}
          </RightItem>
        </AudioContainer>
        <AudioContainer>
          <LeftItem>{t1("examination-period")}</LeftItem>
          <DateRangeItem>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              locale={currentLanguage}
              maxDate={false}
              minDate={true}
            />
            {error.status === "passed" && error.error && (
              <div className="red-text">{t1(error.error)}</div>
            )}
          </DateRangeItem>
        </AudioContainer>

        <ButtonsContainer>
          <Button
            color="#FFFFFF"
            fontSize={16}
            fontWeight={700}
            lineHeight="16px"
            background={"#3B3B3B"}
            padding="13px 24px 11px"
            name={t1("save-as-draft")}
            bdr="6px"
            border="none"
            onClick={handleSaveTempDraft}
            disabled={!testName || stressDraftUpdating}
          />
          <Button
            color="#FFFFFF"
            fontSize={16}
            fontWeight={700}
            lineHeight="16px"
            background={"#2AC769"}
            padding="13px 24px 11px"
            name={t("create-new")}
            bdr="6px"
            border="none"
            onClick={handleOpenModal}
            disabled={!testName || stressDraftUpdating}
          />
        </ButtonsContainer>
        {isVisibleModal && (
          <ConfirmModal
            isVisible={isVisibleModal}
            handleCancel={() => setIsVisibleModal(false)}
            handleOk={handleConfirm}
          >
            <div>{t("notification-will-be-sent")}</div>
            <div>{t("are-you-sure")}</div>
          </ConfirmModal>
        )}
        {isVisibleModalLeave && (
          <ConfirmModal
            isVisible={isVisibleModalLeave}
            handleCancel={() => setIsVisibleModalLeave(false)}
            handleOk={handleCancel}
          >
            <div
              style={{
                fontWeight: 500,
                fontSize: 14,
                lineHeight: "20px",
              }}
            >
              {t1("confirm-leaving")}
            </div>
          </ConfirmModal>
        )}
      </Box>
    </>
  );
};

export default EditDraft;
